'use client';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { cn } from '../cn';
import { TableSkeleton } from './table-skeleton';
import { DataTableFilter, applyFilter, isFilterActive } from './data-table-filter';
import { DataTablePagination } from './data-table-pagination';
import type {
  DataTableProps,
  ColumnDef,
  SortState,
  FilterState,
  FilterValue,
} from './data-table-types';

/** Detectează dacă viewport-ul e mobile (< 768px). SSR-safe: false initial. */
function useIsMobileDataTable(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

/**
 * Componentă generică de tabel de date cu sortare, filtrare per coloană,
 * selecție bulk, paginare client-side, density toggle, persistență sort/density
 * în localStorage. Headless logic (sort/filter în-house) + UI custom Tailwind.
 *
 * Folosit ca fundație pentru shells specializate (ex. DocumentListShell pentru
 * documente fiscale). NU include KPIs / saved views / drawer / date-picker —
 * acestea aparțin layerului superior.
 */
export function DataTable<TData>({
  data,
  columns,
  rowId,
  sort: sortControlled,
  defaultSort = null,
  onSortChange,
  filters: filtersControlled,
  defaultFilters = {},
  onFiltersChange,
  selection: selectionControlled,
  onSelectionChange,
  page: pageControlled,
  pageSize: pageSizeProp = 25,
  total: totalProp,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  renderRowActions,
  renderBulkActions,
  renderRowExpansion,
  expandedRowIds: expandedControlled,
  onExpandToggle,
  density: densityControlled,
  striped = false,
  hoverHighlight = true,
  stickyBulkBar = false,
  emptyState,
  loading = false,
  error,
  persistKey,
  ariaLabel,
  className,
  onRowClick,
  onRowDoubleClick,
  renderMobileCard,
  renderFooter,
  disableMobileCards = false,
  groupBy,
  renderGroupHeader,
  groupOrder,
}: DataTableProps<TData>) {
  const isMobile = useIsMobileDataTable();
  // ── Persisted state (sort + density) ────────────────────────────────
  const [sortInternal, setSortInternal] = useState<SortState>(() => loadPersisted(persistKey, 'sort', defaultSort));
  const [densityInternal, setDensityInternal] = useState<'cozy' | 'compact'>(
    () => loadPersisted(persistKey, 'density', 'cozy' as 'cozy' | 'compact'),
  );
  const sort = sortControlled !== undefined ? sortControlled : sortInternal;
  const density = densityControlled ?? densityInternal;
  const setSort = (s: SortState) => {
    if (sortControlled === undefined) {
      setSortInternal(s);
      savePersisted(persistKey, 'sort', s);
    }
    onSortChange?.(s);
  };
  // ── Filters ────────────────────────────────────────────────────────
  const [filtersInternal, setFiltersInternal] = useState<FilterState>(defaultFilters);
  const filters = filtersControlled ?? filtersInternal;
  const setFilters = (next: FilterState) => {
    if (filtersControlled === undefined) setFiltersInternal(next);
    onFiltersChange?.(next);
  };
  const updateFilter = (colId: string, value: FilterValue | undefined) => {
    const next = { ...filters };
    if (value === undefined) delete next[colId]; else next[colId] = value;
    setFilters(next);
    setPage(1); // reset la prima pagină când filtrul se schimbă
  };

  // ── Selection ──────────────────────────────────────────────────────
  const [selectionInternal, setSelectionInternal] = useState<Set<string>>(new Set());
  const selection = selectionControlled ?? selectionInternal;
  const setSelection = (s: Set<string>) => {
    if (selectionControlled === undefined) setSelectionInternal(s);
    onSelectionChange?.(s);
  };
  const toggleRow = (id: string) => {
    const next = new Set(selection);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelection(next);
  };

  /**
   * Handler global pentru click pe rand. Pattern Notion/Linear:
   *  - Cand selection e activa (size > 0) si selectia e suportata, click pe
   *    rand TOGGLE checkbox-ul (in loc sa cheme onRowClick).
   *  - Cand selection e goala SAU selectia nu e suportata, click cheama
   *    onRowClick (navigare normala).
   *
   * Beneficiu: useri pot selecta multiplu fluent fara click-uri precise pe
   * checkbox, fara teama de a pierde selectia accidental.
   */
  const handleRowClick = (row: TData) => {
    if (showSelection && selection.size > 0) {
      toggleRow(rowId(row));
      return;
    }
    onRowClick?.(row);
  };

  // ── Pagination ─────────────────────────────────────────────────────
  const [pageInternal, setPageInternal] = useState(1);
  const [pageSizeInternal, setPageSizeInternal] = useState(pageSizeProp);
  const page = pageControlled ?? pageInternal;
  const pageSize = onPageSizeChange !== undefined ? pageSizeProp : pageSizeInternal;
  const setPage = (p: number) => {
    if (pageControlled === undefined) setPageInternal(p);
    onPageChange?.(p);
  };

  // ── Compute filtered + sorted + paginated rows ──────────────────────
  const filtered = useMemo(() => {
    if (Object.keys(filters).length === 0) return data;
    return data.filter((row) => {
      for (const col of columns) {
        if (!col.filter) continue;
        const fv = filters[col.id];
        if (!isFilterActive(fv)) continue;
        const v = col.accessor ? col.accessor(row) : (row as Record<string, unknown>)[col.id];
        if (!applyFilter(v, fv, col.filter.type)) return false;
      }
      return true;
    });
  }, [data, columns, filters]);

  const sorted = useMemo(() => {
    // Server-side (paginat + sortare controlată): rândurile vin deja sortate de backend —
    // nu re-sortăm client-side (localeCompare poate diferi de colația Postgres → reordonare subtilă).
    if (totalProp !== undefined && sortControlled !== undefined) return filtered;
    if (!sort) return filtered;
    const col = columns.find((c) => c.id === sort.col);
    if (!col || !col.accessor) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = col.accessor!(a);
      const vb = col.accessor!(b);
      if (va == null && vb == null) return 0;
      if (va == null) return dir;
      if (vb == null) return -dir;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'ro') * dir;
    });
  }, [filtered, sort, columns, totalProp, sortControlled]);

  const total = totalProp ?? sorted.length;
  const paged = useMemo(() => {
    if (totalProp !== undefined) return sorted; // server-side: data e deja paginat
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize, totalProp]);

  // ── Helpers ────────────────────────────────────────────────────────
  const cycleSort = (colId: string) => {
    if (!sort || sort.col !== colId) {
      setSort({ col: colId, dir: 'asc' });
    } else if (sort.dir === 'asc') {
      setSort({ col: colId, dir: 'desc' });
    } else {
      setSort(null);
    }
  };

  const allOnPageSelected = paged.length > 0 && paged.every((r) => selection.has(rowId(r)));
  const toggleAllOnPage = () => {
    const next = new Set(selection);
    if (allOnPageSelected) {
      paged.forEach((r) => next.delete(rowId(r)));
    } else {
      paged.forEach((r) => next.add(rowId(r)));
    }
    setSelection(next);
  };

  const showSelection = !!onSelectionChange || !!renderBulkActions;
  const showActions = !!renderRowActions;
  const showExpansion = !!renderRowExpansion;
  const totalCols = columns.length + (showSelection ? 1 : 0) + (showActions ? 1 : 0) + (showExpansion ? 1 : 0);
  const leadingCols = (showExpansion ? 1 : 0) + (showSelection ? 1 : 0);

  // Grupare opt-in: partiționează rândurile paginate pe grupuri (păstrând ordinea de sort
  // din interiorul grupului), în ordinea `groupOrder`. null când groupBy nu e setat.
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map<string, TData[]>();
    for (const row of paged) {
      const k = groupBy(row);
      const arr = map.get(k);
      if (arr) arr.push(row); else map.set(k, [row]);
    }
    let keys = [...map.keys()];
    if (groupOrder) {
      keys = keys.sort((a, b) => {
        const ia = groupOrder.indexOf(a); const ib = groupOrder.indexOf(b);
        return (ia === -1 ? 1e9 : ia) - (ib === -1 ? 1e9 : ib);
      });
    }
    return keys.map((k) => ({ key: k, rows: map.get(k)! }));
  }, [paged, groupBy, groupOrder]);

  // ── Expansion state ─────────────────────────────────────────────────
  const [expandedInternal, setExpandedInternal] = useState<Set<string>>(new Set());
  const expanded = expandedControlled ?? expandedInternal;
  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    if (expandedControlled === undefined) setExpandedInternal(next);
    onExpandToggle?.(next);
  };

  // ── Render ──────────────────────────────────────────────────────────
  if (loading) return <div className={cn('dt-default-skin rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900', className)}><TableSkeleton rows={pageSize} /></div>;

  if (error) {
    return (
      <div className={cn('rounded-lg border border-red-200 bg-red-50 p-6 text-center', className)} role="alert">
        <p className="text-sm font-medium text-red-700">{error.message}</p>
        {error.onRetry && (
          <button type="button" onClick={error.onRetry} className="mt-3 rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">
            Reîncearcă
          </button>
        )}
      </div>
    );
  }

  const isEmpty = paged.length === 0;
  const cellPaddingY = density === 'compact' ? 'py-1.5' : 'py-2.5';
  const cellFontSize = density === 'compact' ? 'text-xs' : 'text-sm';

  return (
    <div className={cn('dt-default-skin flex flex-col rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900', className)}>
      {/* Bulk actions bar */}
      {showSelection && selection.size > 0 && renderBulkActions && (
        <div
          className={cn(
            'flex items-center gap-3 border-b border-neutral-200 bg-primary-50 px-3 py-2 text-sm',
            stickyBulkBar && 'sticky top-0 z-30',
          )}
        >
          <span className="font-medium text-primary-900">{selection.size} selectat{selection.size === 1 ? '' : 'e'}</span>
          <div className="flex flex-1 items-center gap-2">{renderBulkActions(selection)}</div>
          <button type="button" onClick={() => setSelection(new Set())} className="rounded px-2 py-1 text-xs text-neutral-600 hover:bg-white">Anulează</button>
        </div>
      )}

      <div role="status" aria-live="polite" className="sr-only">
        {total === 0
          ? 'Niciun rezultat'
          : `${total} ${total === 1 ? 'rezultat' : 'rezultate'}${sort ? ` sortate ${sort.dir === 'asc' ? 'crescător' : 'descrescător'} după ${sort.col}` : ''}`}
      </div>

      {/* Mobile card view (< 768px) — replaces table */}
      {isMobile && !disableMobileCards && (
        <div className="flex flex-col gap-2 p-2">
          {loading ? (
            <div className="py-10 text-center text-sm text-neutral-500">Se încarcă…</div>
          ) : isEmpty ? (
            <div className="px-6 py-16 text-center">
              {emptyState ?? (
                <div className="flex flex-col items-center gap-2 text-neutral-500">
                  <div className="text-2xl" aria-hidden="true">📭</div>
                  <p className="text-sm font-medium">Niciun rezultat găsit</p>
                </div>
              )}
            </div>
          ) : (
            paged.map((row) => {
              const id = rowId(row);
              if (renderMobileCard) {
                return (
                  <div
                    key={id}
                    onClick={(showSelection || onRowClick) ? () => handleRowClick(row) : undefined}
                    className={cn(
                      'rounded-lg border border-neutral-200 bg-white shadow-sm',
                      (showSelection || onRowClick) && 'cursor-pointer active:bg-neutral-50',
                    )}
                  >
                    {renderMobileCard(row)}
                  </div>
                );
              }
              // Default mobile card: first column as header, rest as label:value rows
              const visibleCols = columns.filter((c) => c.hideAt !== 'sm');
              const [headerCol, ...restCols] = visibleCols;
              return (
                <div
                  key={id}
                  onClick={(showSelection || onRowClick) ? () => handleRowClick(row) : undefined}
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border border-neutral-200 bg-white p-3 shadow-sm',
                    (showSelection || onRowClick) && 'cursor-pointer active:bg-neutral-50',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 text-sm font-semibold text-neutral-900">
                      {headerCol?.cell ? (headerCol.cell(row) as React.ReactNode) : (headerCol?.accessor ? String(headerCol.accessor(row) ?? '') : '')}
                    </div>
                    {showActions && (
                      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                        {renderRowActions!(row)}
                      </div>
                    )}
                  </div>
                  {restCols.length > 0 && (
                    <div className="flex flex-col gap-1 text-xs text-neutral-600">
                      {restCols.map((col) => {
                        const value = col.cell ? col.cell(row) : (col.accessor ? String(col.accessor(row) ?? '') : '');
                        if (value === '' || value === null || value === undefined || value === '—') return null;
                        const headerLabel = typeof col.header === 'string' ? col.header : col.id;
                        return (
                          <div key={col.id} className="flex items-baseline justify-between gap-2">
                            <span className="shrink-0 text-[0.7rem] uppercase tracking-wide text-neutral-400">{headerLabel}</span>
                            <span className={cn('min-w-0 truncate text-right', col.align === 'right' && 'tabular-nums font-medium')}>
                              {value as React.ReactNode}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Table (desktop and when mobile cards disabled) */}
      <div className={cn('overflow-x-auto', isMobile && !disableMobileCards && 'hidden')}>
        <table className={cn('w-full border-collapse', cellFontSize)} aria-label={ariaLabel} aria-busy={loading}>
          <thead className="sticky top-0 z-10">
            <tr>
              {showExpansion && (
                <th scope="col" className="w-8 border-b px-2 py-2">
                  <span className="sr-only">Expandare</span>
                </th>
              )}
              {showSelection && (
                <th scope="col" className="w-10 border-b px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                    aria-label={allOnPageSelected ? 'Deselectează toate rândurile vizibile' : 'Selectează toate rândurile vizibile'}
                  />
                </th>
              )}
              {columns.map((col) => {
                const headerText = typeof col.header === 'function' ? '' : col.header;
                const ariaSort: 'ascending' | 'descending' | 'none' | undefined = col.sortable
                  ? (sort?.col === col.id ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none')
                  : undefined;
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={ariaSort}
                    style={col.width ? { width: typeof col.width === 'number' ? `${col.width}px` : col.width } : undefined}
                    className={cn(
                      // Stil header aliniat cu tabelul .v-table din /facturi: casing
                      // natural (FĂRĂ uppercase), 0.78rem, fără letter-spacing.
                      'border-b px-3 py-2 text-left text-[0.78rem] font-semibold',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.hideAt === 'sm' && 'hidden sm:table-cell',
                      col.hideAt === 'md' && 'hidden md:table-cell',
                      col.hideAt === 'lg' && 'hidden lg:table-cell',
                      col.sticky === 'left' && 'dt-th-sticky-left sticky left-0 z-20',
                      col.sticky === 'right' && 'dt-th-sticky-right sticky right-0 z-20',
                    )}
                  >
                    <div className={cn('inline-flex items-center gap-1', col.align === 'right' && 'flex-row-reverse')}>
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => cycleSort(col.id)}
                          className="inline-flex items-center gap-1 cursor-pointer hover:text-neutral-900"
                        >
                          {typeof col.header === 'function' ? col.header() : col.header}
                          {sort?.col === col.id && (
                            <span aria-hidden="true" className="text-primary-600">{sort.dir === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      ) : (
                        <span>{typeof col.header === 'function' ? col.header() : col.header}</span>
                      )}
                      {col.filter && (
                        <DataTableFilter
                          columnId={col.id}
                          columnLabel={headerText || col.id}
                          config={col.filter}
                          value={filters[col.id]}
                          onChange={(v) => updateFilter(col.id, v)}
                          align={col.align === 'right' ? 'right' : 'left'}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
              {showActions && (
                <th scope="col" className="dt-th-sticky-right sticky right-0 z-20 w-px border-b px-3 py-2 text-right">
                  <span className="sr-only">Acțiuni</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={columns.length + (showSelection ? 1 : 0) + (showActions ? 1 : 0) + (showExpansion ? 1 : 0)} className="px-6 py-16 text-center">
                  {emptyState ?? (
                    <div className="flex flex-col items-center gap-2 text-neutral-500">
                      <div className="text-2xl" aria-hidden="true">📭</div>
                      <p className="text-sm font-medium">Niciun rezultat găsit</p>
                      <p className="text-xs">Încearcă să modifici filtrele sau caută altfel.</p>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              (() => {
                // Listă de randare: cu groupBy, inserează un rând-antet de secțiune între grupuri.
                type RI = { gKey: string; gRows: TData[] } | { row: TData; rIdx: number };
                const items: RI[] = [];
                if (groupBy && grouped) {
                  let gi = 0;
                  for (const g of grouped) {
                    items.push({ gKey: g.key, gRows: g.rows });
                    for (const r of g.rows) items.push({ row: r, rIdx: gi++ });
                  }
                } else {
                  paged.forEach((r, i) => items.push({ row: r, rIdx: i }));
                }
                return items.map((it) => {
                  if ('gKey' in it) {
                    return (
                      <tr key={`__grp_${it.gKey}`} className="dt-group-header">
                        <td colSpan={totalCols} className="dt-group-header-cell" style={{ background: 'var(--v-bg-deeper, #f4f4f3)' }}>
                          {renderGroupHeader ? renderGroupHeader(it.gKey, it.gRows) : it.gKey}
                        </td>
                      </tr>
                    );
                  }
                const row = it.row;
                const idx = it.rIdx;
                const id = rowId(row);
                const sel = selection.has(id);
                const isExpanded = expanded.has(id);
                // Fundal OPAC pentru celulele sticky (left/right + acțiuni). Altfel, pe
                // tabele cu overflow orizontal, conținutul coloanelor alunecă pe sub celula
                // lipită (bg-inherit = transparent pe rândul fără fundal) și „transpare"
                // (ex: creionul peste status în lista de recepții). Reflectă stările rândului
                // (selectat/expandat/striped) + hover, în variante opace.
                // Variante OPACE ale fundalurilor de rând (care sunt semi-transparente:
                // /40, /30). Default = culoarea suprafeței (singura care cere variantă dark,
                // fiindcă rândul default e transparent peste card). Tintele urmează rândul.
                const stickyBaseBg = sel
                  ? 'bg-primary-50'
                  : isExpanded
                    ? 'bg-neutral-50'
                    : (striped && idx % 2 === 1)
                      ? 'bg-neutral-50'
                      : 'bg-white dark:bg-neutral-950';
                const stickyCellBg = cn(stickyBaseBg, hoverHighlight && 'group-hover/dtrow:bg-neutral-50');
                return (
                  <Fragment key={id}>
                    <tr
                      className={cn(
                        'group/dtrow border-b border-neutral-100 transition-colors',
                        striped && idx % 2 === 1 && 'bg-neutral-50/40',
                        hoverHighlight && 'hover:bg-neutral-50',
                        sel && 'bg-primary-50/30',
                        isExpanded && 'border-b-0 bg-neutral-50',
                        onRowClick && 'cursor-pointer',
                      )}
                      onClick={(showSelection || onRowClick) ? () => handleRowClick(row) : undefined}
                      onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                    >
                      {showExpansion && (
                        <td className={cn('px-2', cellPaddingY)}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(id); }}
                            className="flex h-6 w-6 items-center justify-center rounded text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Restrânge rând' : 'Expandează rând'}
                          >
                            <span aria-hidden="true" className={cn('inline-block transition-transform', isExpanded && 'rotate-90')}>›</span>
                          </button>
                        </td>
                      )}
                      {showSelection && (
                        <td className={cn('px-3', cellPaddingY)}>
                          <input
                            type="checkbox"
                            checked={sel}
                            onChange={(e) => { e.stopPropagation(); toggleRow(id); }}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Selectează rândul ${id}`}
                          />
                        </td>
                      )}
                      {columns.map((col, colIdx) => {
                        const value = col.cell ? col.cell(row) : (col.accessor ? String(col.accessor(row) ?? '') : '');
                        return (
                          <td
                            key={col.id}
                            className={cn(
                              'px-3', cellPaddingY,
                              colIdx === 0 && col.align !== 'right' && 'font-medium text-neutral-900 dark:text-neutral-100',
                              col.align === 'right' && 'text-right tabular-nums font-semibold text-neutral-900 dark:text-neutral-100',
                              col.align === 'center' && 'text-center',
                              col.hideAt === 'sm' && 'hidden sm:table-cell',
                              col.hideAt === 'md' && 'hidden md:table-cell',
                              col.hideAt === 'lg' && 'hidden lg:table-cell',
                              col.sticky === 'left' && `sticky left-0 ${stickyCellBg}`,
                              col.sticky === 'right' && `sticky right-0 ${stickyCellBg}`,
                            )}
                          >
                            {value as React.ReactNode}
                          </td>
                        );
                      })}
                      {showActions && (
                        <td className={cn('sticky right-0 px-3 text-right', stickyCellBg, cellPaddingY)}>
                          {renderRowActions!(row)}
                        </td>
                      )}
                    </tr>
                    {showExpansion && isExpanded && (
                      <tr className="border-b border-neutral-100 bg-neutral-50">
                        <td colSpan={totalCols} className="px-3 py-3">
                          {renderRowExpansion!(row)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
                });
              })()
            )}
          </tbody>
          {renderFooter && !isEmpty && (
            <tfoot>
              {renderFooter({ pageRows: paged, allRows: sorted, colCount: totalCols, leadingCols })}
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {!isEmpty && total > pageSize && (
        <DataTablePagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={onPageSizeChange ?? (pageControlled === undefined ? (s) => { setPageSizeInternal(s); setPage(1); } : undefined)}
          pageSizeOptions={pageSizeOptions}
          ariaLabel={`Paginare ${ariaLabel}`}
        />
      )}
    </div>
  );
}

// ── localStorage helpers ────────────────────────────────────────────────
function loadPersisted<T>(key: string | undefined, sub: string, fallback: T): T {
  if (!key || typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(`${key}:${sub}`);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function savePersisted<T>(key: string | undefined, sub: string, value: T): void {
  if (!key || typeof window === 'undefined') return;
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(`${key}:${sub}`);
    } else {
      window.localStorage.setItem(`${key}:${sub}`, JSON.stringify(value));
    }
  } catch {
    // ignore quota/security errors
  }
}
